import type { ItemProperties } from "@/types";

/**
 * Reading a POI's array-valued property as table rows.
 *
 * Both tabular POI blocks — the plain table and the files list — read an array of
 * objects out of one property and show a configured subset of their keys, in the
 * configured order.
 */

export type TableRow = Record<string, unknown>;

/**
 * The entries to render, whole.
 *
 * They are not narrowed to the configured columns: the files list needs each
 * entry's `fileUrl` to link its `fileName`, and a tenant should not have to show
 * a column of raw URLs to get links. The columns decide what is *displayed* —
 * that's the caller's business, since it also owns the header row.
 *
 * A block with no configured columns has nothing to display, so it renders
 * nothing. Legacy produced a row of empty objects instead, which came out as a
 * table with no headers and no cells.
 */
export function tableEntries(
  properties: ItemProperties | undefined,
  field: string | undefined,
  columns: string[] | undefined
): TableRow[] {
  if (!field || !columns?.length) return [];

  const value = properties?.[field];
  if (!Array.isArray(value)) return [];

  return value.filter(
    (entry): entry is TableRow => typeof entry === "object" && entry !== null
  );
}

/** A Squidex date-time, which is the only cell value that gets reformatted. */
const DATE_TIME = /^(\d{4}-\d{2}-\d{2})T\d{2}:\d{2}:\d{2}Z$/;

/**
 * Show a date-time cell as its calendar date.
 *
 * The date part is taken from the string rather than parsed and re-formatted:
 * Squidex stores date-only fields as midnight UTC, so converting to the viewer's
 * local time would show the previous day everywhere west of Greenwich. It also
 * keeps the server and client renders identical, which the POI route needs.
 */
export function formatCell(value: unknown): unknown {
  if (typeof value !== "string") return value;
  return DATE_TIME.exec(value)?.[1] ?? value;
}

/** File types we ship an icon for; anything else gets the generic one. */
const ICON_TYPES = new Set([
  "dwg",
  "e57",
  "ifc",
  "las",
  "laz",
  "msa",
  "others",
  "pdf",
  "rvt",
  "xlsx",
]);

/** The file-type icon for a files-list row's `fileExtension`, under `public/`. */
export function fileIconPath(extension: unknown): string {
  const name =
    typeof extension === "string" ? extension.replace(/^\./, "") : "";
  return `/images/files/${ICON_TYPES.has(name) ? name : "others"}.png`;
}
